import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Layout, Button } from '@storable/canaveral-components';
import pageTypes from 'utils/pageTypes';
import { Tooltip, OverlayTrigger } from 'react-bootstrap';
import { manifest as sectionManifest } from 'components/sections/manifest';
import { manifest as widgetManifest } from 'components/widgets/manifest';
import rcWidgetManifest from 'components/widgets/rentalCenterWidgets/manifest';
import { FACILITY_ADD_ONS, NON_FACILITY_ADD_ONS, BUILDER } from 'shared/abilities';
import Authorization from 'containers/Authorization';
import { sortAlphabetically } from '../../../../../data/lib/sort';
import Icon from '../../../../../modules/react/components/stateless/Icon';
import ComponentListEntry from './ComponentListEntry';
import AddOnsList from './AddOnsList';
import style from './ComponentLibrary.scss';

const OPTIONS = 'options';
const SELECTION_SECTION = 'section';
const SELECTION_WIDGET = 'widget';
const SELECTION_ADDON = 'addon';
const ENT_SECTIONS = 'ENT_SECTIONS';
const ENT_WIDGETS = 'ENT_WIDGETS';
const RC_WIDGETS = 'RC_WIDGETS';

const { Navbar } = Layout;

const addWidgetToArrayIfAccepted = array => ({ subsections }) => {
  subsections.forEach(({ accepts }) => {
    if (!accepts) {
      return;
    }

    if (Array.isArray(accepts)) {
      accepts.forEach(type => array.push(type));
      return;
    }

    array.push(accepts);
  });
};

/**
 * This component provides the sections/widgets to drag and drop onto a page builder.
 */
export default class ComponentLibrary extends React.Component {
  static propTypes = {
    show: PropTypes.bool,
    pageType: PropTypes.string.isRequired,
    presentPage: PropTypes.object,
    revise: PropTypes.func,
    saveVersion: PropTypes.func,
    onSectionStartDragging: PropTypes.func.isRequired,
    onSectionStopDragging: PropTypes.func.isRequired,
    onWidgetStartDragging: PropTypes.func.isRequired,
    onWidgetStopDragging: PropTypes.func.isRequired,
    onToggleComponentLibrary: PropTypes.func.isRequired,
    popUp: PropTypes.any,
    onShowPopupModal: PropTypes.func,
    accessEnterpriseOptions: PropTypes.bool.isRequired,
    accessRentalCenterOptions: PropTypes.bool,
    // new prop to hide the add ons option for components not hooked up to the redux store
    hideAddOns: PropTypes.bool,
  };

  static defaultProps = {
    show: false,
    presentPage: {},
  };

  state = {
    selection: OPTIONS,
  };

  getAvailableSections = () => {
    const { pageType } = this.props;
    const page = pageTypes[pageType];
    const allSectionManifest = sectionManifest;

    return allSectionManifest
      .filter(({ type, region, hidden, flipper }) => {
        let allowed = true;

        if (flipper) {
          allowed = this.props[flipper];
        }
        return page[region].accepts(type) && !hidden && allowed;
      })
      .sort((a, b) => sortAlphabetically(a.name, b.name));
  };

  getAvailableWidgets = () => {
    const widgets = [];
    const addToWidgetsIfAccepted = addWidgetToArrayIfAccepted(widgets);

    this.getAvailableSections().forEach(addToWidgetsIfAccepted);

    return widgetManifest
      .filter(({ type, hidden }) => widgets.indexOf(type) !== -1 && !hidden)
      .sort((a, b) => sortAlphabetically(a.name, b.name));
  }

  getAvailableRCWidgets = () => {
    const widgets = [];
    const addToWidgetsIfAccepted = addWidgetToArrayIfAccepted(widgets);

    this.getAvailableSections().forEach(addToWidgetsIfAccepted);

    return rcWidgetManifest
      .filter(({ type, hidden }) => widgets.indexOf(type) !== -1 && !hidden)
      .sort((a, b) => sortAlphabetically(a.name, b.name));
  }

  closeLibrary = () => {
    this.setState({ selection: OPTIONS });
    this.props.onToggleComponentLibrary();
  }

  renderArrow = (isActive) => {
    const classNames = [
      'icon',
      `icon-${isActive ? 'collapse' : 'expand'}`,
      style.mobileCaret,
    ];

    return <span className={classNames.join(' ')} />;
  }

  renderTitle() {
    const titles = {
      options: 'Add to your site',
      section: 'Section',
      widget: 'Widget',
      addon: 'Add-On',
      ENT_SECTIONS: 'Enterprise Sections',
      ENT_WIDGETS: 'Enterprise Widgets',
      RC_WIDGETS: 'Rental Center Widgets',
    };

    return titles[this.state.selection];
  }

  renderBackArrow() {
    return this.state.selection !== OPTIONS ?
      <Button
        context="bare"
        onClick={() => this.setState({ selection: OPTIONS })}
        icon="back-arrow"
        className={style.backIcon}
        label="Back"
      />
      :
      <span />;
  }

  renderTooltip(tooltipArea) {
    const drawerTooltipText = `You do not have permissions to add ${tooltipArea} to your site.
                               Please contact support.`;

    return (
      <OverlayTrigger
        placement="right"
        overlay={
          <Tooltip className={style.toolTip} id={tooltipArea.replace(/\s+/g, '')}>
            <div className={style.tooltipInner}>
              {drawerTooltipText}
            </div>
          </Tooltip>
        }
      >
        <span className={style.tooltipIcon}>
          <Icon id="tool-tip" label="Not Authorized" />
        </span>
      </OverlayTrigger>
    );
  }

  renderComponents() {
    const {
      pageType,
      presentPage,
      revise,
      saveVersion,
      onSectionStartDragging,
      onSectionStopDragging,
      onWidgetStartDragging,
      onWidgetStopDragging,
      popUp,
      onShowPopupModal,
      accessEnterpriseOptions,
      accessRentalCenterOptions,
      hideAddOns,
    } = this.props;

    const componentListProps = {
      pageType,
      renderArrow: this.renderArrow,
      darkClassName: style.darkBackground,
      dimClassName: style.dimBackground,
    };

    const widgetProps = {
      ...componentListProps,
      componentStartDragging: onWidgetStartDragging,
      componentStopDragging: onWidgetStopDragging,
    };

    const sectionProps = {
      ...componentListProps,
      componentStartDragging: onSectionStartDragging,
      componentStopDragging: onSectionStopDragging,
    };

    const addons = presentPage.facilityId ? FACILITY_ADD_ONS : NON_FACILITY_ADD_ONS;

    switch (this.state.selection) {
      case SELECTION_SECTION:
        return (
          <ComponentListEntry
            {...sectionProps}
            availableComponents={this.getAvailableSections}
          />
        );

      case SELECTION_WIDGET:
        return (
          <ComponentListEntry
            {...widgetProps}
            availableComponents={this.getAvailableWidgets}

          />
        );

      case SELECTION_ADDON:
        return (
          <AddOnsList
            presentPage={presentPage}
            revise={revise}
            saveVersion={saveVersion}
            popUp={popUp}
            onShowPopupModal={onShowPopupModal}
          />
        );

      case RC_WIDGETS:
        return (
          <ComponentListEntry
            {...widgetProps}
            availableComponents={this.getAvailableRCWidgets}
          />
        );

      case OPTIONS:
        return (
          <div>
            <Authorization action="manage" subject={BUILDER}>
              {authorized => (
                <div
                  className={style.option}
                  onClick={() => authorized && this.setState({ selection: SELECTION_SECTION })}
                >
                  <span className={cx(!authorized && style.disabled)}>Section</span>
                  {!authorized && this.renderTooltip('a section')}
                </div>
              )}
            </Authorization>
            <Authorization action="manage" subject={BUILDER}>
              {authorized => (
                <div
                  className={style.option}
                  onClick={() => authorized && this.setState({ selection: SELECTION_WIDGET })}
                >
                  <span className={cx(!authorized && style.disabled)}>Widget</span>
                  {!authorized && this.renderTooltip('a widget')}
                </div>
              )}
            </Authorization>
            {!hideAddOns && (
              <Authorization action="manage" subject={[addons, BUILDER]}>
                {authorized => (
                  <div
                    className={style.option}
                    onClick={() => authorized && this.setState({ selection: SELECTION_ADDON })}
                  >
                    <span className={cx(!authorized && style.disabled)}>Add-On</span>
                    {!authorized && this.renderTooltip('an add-on')}
                  </div>
                )}
              </Authorization>
            )}
            {accessEnterpriseOptions &&
              <Authorization action="manage" subject={BUILDER}>
                {authorized => authorized && (
                  <div
                    className={style.option}
                    onClick={() => this.setState({ selection: ENT_SECTIONS })}
                  >
                    <span>Enterprise Sections</span>
                  </div>
                )}
              </Authorization>
            }
            {accessEnterpriseOptions &&
              <Authorization action="manage" subject={BUILDER}>
                {authorized => authorized && (
                  <div
                    className={style.option}
                    onClick={() => this.setState({ selection: ENT_WIDGETS })}
                  >
                    <span>Enterprise Widgets</span>
                  </div>
                )}
              </Authorization>
            }
            {accessRentalCenterOptions &&
              <Authorization action="manage" subject={BUILDER}>
                {authorized => authorized && (
                  <div
                    className={style.option}
                    onClick={() => this.setState({ selection: RC_WIDGETS })}
                  >
                    <span>Rental Center Widgets</span>
                  </div>
                )}
              </Authorization>
            }
          </div>
        );

      default:
        return null;
    }
  }

  render() {
    const {
      menu, title, closeIcon, componentList,
    } = style;

    return (
      <Navbar.Panel show={this.props.show}>
        <div className={menu}>
          <div className={title}>
            { this.renderBackArrow() }
            <span>{this.renderTitle()}</span>
            <Button
              icon="exit"
              onClick={this.closeLibrary}
              className={closeIcon}
              context="bare"
              label="Close"
            />
          </div>
          <ul className={componentList}>
            {this.renderComponents()}
          </ul>
        </div>
      </Navbar.Panel>
    );
  }
}
